import "./RecentVisits.scss";
import { Button, Divider } from "antd";
import { RequestChannel } from "@vyne-shared/api/VynePayments/responses";
import { RightOutlined } from "@ant-design/icons";
import { type StrictlyBasisPoints, stringFromBasisPoints } from "@vyne-shared/utils/currency";
import { VyneTable } from "@vyne-shared/components/vyneTable/VyneTable";
import { useAppointments } from "api/clients/useAppointments";
import type { AppointmentResult, ExistingPatientResult } from "api/responses";
import type { ColumnType } from "antd/es/table";

/** Properties of component showing recent patient appointments. */
interface RecentVisitsProps {
    /** When true, recent visits are shown.
     * When false, only a header is shown (like a collapsed accordion item). */
    readonly expanded: boolean;

    /** Callback to execute when the recent visits header is clicked. */
    readonly onHeaderClick: () => void;

    /** Callback to execute when a patient is selected by clicking on a recent appointment. */
    readonly onSelectExistingPrp: (prp: ExistingPatientResult) => void;
}

const columns: ColumnType<AppointmentResult>[] = [
    {
        title: "Name",
        dataIndex: "patientName",
    },
    {
        title: "D.O.B.",
        dataIndex: "dateOfBirth",
    },
    {
        title: "Last Appt",
        dataIndex: "appointmentDate", // appointmentTime not shown
    },
    {
        title: "Balance",
        dataIndex: "balance",
        render: (value) => stringFromBasisPoints(value as StrictlyBasisPoints),
    },
];

/** Component showing recent patient appointments. */
export const RecentVisits = ({ expanded, onHeaderClick, onSelectExistingPrp }: RecentVisitsProps) => {
    const rotate = expanded ? 90 : 0;
    const { isPending, isError, data } = useAppointments(expanded);

    return (
        <>
            <Divider className="search-recent-or-text">Or</Divider>
            <div className="vyne-recent-visits">
                <Button type="text" className="vyne-recent-visits-header" onClick={onHeaderClick}>
                    <RightOutlined rotate={rotate} />
                    Recent Patient Visits
                </Button>
                {expanded && (
                    <VyneTable
                        name="Recent Patient Visits"
                        loading={isPending}
                        erroring={isError}
                        rowKey="appointmentId"
                        dataSource={data}
                        columns={columns}
                        pagination={false}
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    // NOTE: This is a potentially risky lie--other parts of the code may depend on
                                    // these facts, and may break because we are filling them in with junk data.
                                    // Consider using ExistingPatientQuery with an ID to obtain real data in the future.
                                    onSelectExistingPrp({
                                        id: record.id,
                                        pmsId: record.pmsId,
                                        balance: record.balance,
                                        familyName: record.patientName, // put concatenated full name in family name
                                        givenName: "",
                                        updateDate: 0,
                                        requestChannel: RequestChannel.Both,
                                        allowCommunication: false,
                                        hasOutstandingClaims: false,
                                    });
                                },
                            };
                        }}
                        locale={{ emptyText: "No recent visits found." }}
                    />
                )}
            </div>
        </>
    );
};
